import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import CardSaveButton from '../common/CardSaveButton';

const ProposalCard = ({ proposal, onResolved }) => {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  const act = async (action) => {
    setLoading(action);
    setError('');
    try {
      if (action === 'accept') {
        await axios.post(`/api/cashflow/proposals/${proposal.id}/accept`);
      } else if (action === 'decline') {
        await axios.post(`/api/cashflow/proposals/${proposal.id}/decline`);
      } else if (action === 'snooze') {
        const until = new Date();
        until.setDate(until.getDate() + 1);
        await axios.post(`/api/cashflow/proposals/${proposal.id}/snooze`, {
          until: until.toISOString(),
        });
      }
      onResolved?.(proposal.id, action);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not apply this proposal. Try Add / edit expenses instead.'
      );
      console.error(e);
    } finally {
      setLoading(null);
    }
  };

  if (!proposal || proposal.status !== 'proposed') return null;

  return (
    <motion.div
      className="aura-card-emphasis mb-3"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-label text-ink-muted">
          {proposal.risk_tier === 'info' ? 'Insight' : 'Awaiting your OK'}
        </span>
        <div className="flex items-center gap-2">
          <CardSaveButton
            card={{
              type: 'proposal',
              id: proposal.id,
              title: proposal.title,
              content: proposal.rationale,
              proposal,
            }}
            sourceSurface="cashflow"
            showLabel
          />
          <span className="text-label text-ink-faint">{proposal.kind?.replace(/_/g, ' ')}</span>
        </div>
      </div>
      <h4 className="font-sans text-h3 text-ink mb-1">{proposal.title}</h4>
      <p className="text-body text-ink-muted mb-3">{proposal.rationale}</p>
      {proposal.suggested_action?.tool && (
        <p className="text-label text-ink-muted mb-3">
          If you accept, Aura will: {proposal.suggested_action.tool.replace(/_/g, ' ')}
        </p>
      )}
      <p className="text-label text-ink-muted mb-3 italic">
        Suggestion only — nothing changes until you confirm.
      </p>
      {error && <p className="text-danger text-label mb-2">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!!loading}
          onClick={() => act('accept')}
          className="btn-primary text-label py-1.5 px-3"
        >
          {loading === 'accept' ? '…' : 'Accept'}
        </button>
        <button
          type="button"
          disabled={!!loading}
          onClick={() => act('decline')}
          className="btn-ghost text-label py-1.5 px-3 border border-hairline"
        >
          Decline
        </button>
        <button
          type="button"
          disabled={!!loading}
          onClick={() => act('snooze')}
          className="btn-ghost text-label py-1.5 px-3"
        >
          Snooze 1 day
        </button>
      </div>
    </motion.div>
  );
};

export default ProposalCard;
